import React, { useEffect, useState } from "react";
import { CreateGroupOrderPropTypes, CreateGroupOrderLabels } from "./types";
import useViewport from "../../Helpers/hooks/useViewport";
import { default as CreateGroupOrderWebLayout } from "./layout.web";
import { default as CreateGroupOrderMobileLayout } from "./layout.mobile";
import { getImageAsset } from "../../Helpers/get-assets";
import { DELIVERY_TYPES } from "../Topbar";
import i18next from "i18next";
import useCustomEvent from "../../Helpers/hooks/useCustomEvent";
import { useDeliveryTimes } from "../../Api/deliveryTimesApi";
import {
    formatDateToHours,
    formatTimeslotEntryFromCart,
} from "../../Helpers/timeslot-utils";
import { useFormik } from "formik";
import TimeslotPopup from "../TimeslotPopup";
import { isToday } from "../../Helpers/date-utils";
import { containsNonPrintables, isRequired } from "../../Helpers/validators";
import QrCodePopup from "../QrCodePopup";
import useCopyToClipboard from "../../Helpers/hooks/useCopyToClipboard";
import NotificationPopup from "../Popup/NotificationPopup";
import { Check } from "react-feather";
import { useUser } from "../../Api/userApi";
import useGroupOrder, { GROUP_ORDER_BUFFER } from "../../global/useGroupOrder";
import useDeliveryTypeChangeListener from "../../Helpers/hooks/useDeliveryTypeChangeListener";
import useAreaCookie from "../../Helpers/hooks/useAreaCookie";
import { getLocalisedDayOfWeek } from "../../Helpers/date-utils";

export const CREATE_GROUP_ORDER_STEPS = {
    INFO: "INFO",
    SHARE: "SHARE",
};

const CreateGroupOrder = (props) => {
    const { isDesktop } = useViewport();
    const cookie = useAreaCookie();
    const { address, area } = cookie;
    const [step, setStep] = useState(CREATE_GROUP_ORDER_STEPS.INFO);
    const [timeslotPopupOpen, setOpenTimeslotPopup] = useState(false);
    const [qrOpen, setQrPopup] = useState(false);
    const [copySuccessPopup, setCopySuccess] = useState(false);
    const { user } = useUser();
    const { triggerEvent } = useCustomEvent("trigger-address-popup");
    const { earliestTimeslot, refetchTimeslots } = useDeliveryTimes({
        buffer: GROUP_ORDER_BUFFER,
    });
    const { copy } = useCopyToClipboard();
    const isPickup = area?.type === DELIVERY_TYPES.PICKUP;
    const { createGroupOrder, inviteUrl, loading } = useGroupOrder();

    const formik = useFormik({
        initialValues: {
            name: "",
            timeslot: null,
            earlyOnLocation: false,
        },
        onSubmit: async ({ name, timeslot, earlyOnLocation }) => {
            if (isRequired(name) || containsNonPrintables(name)) {
                formik.setFieldError("name", i18next.t("form.errors.required"));
                return;
            }

            const selectedSlot = timeslot || earliestTimeslot;
            formik.setFieldValue("timeslot", selectedSlot);
            const response = await createGroupOrder({
                name,
                timeslot: selectedSlot,
                earlyOnLocation,
            });

            if (response.ok) {
                setStep(CREATE_GROUP_ORDER_STEPS.SHARE);
            }
        },
    });

    useDeliveryTypeChangeListener({
        onLocationChangeCb: () => {
            refetchTimeslots();
            formik.setValues({
                name: formik.values.name,
                timeslot: null,
                earlyOnLocation: false,
            });
        },
    });

    // Prefill username if user logged in
    useEffect(() => {
        if (user && formik.values.name === "") {
            formik.setFieldValue("name", user?.first_name || "");
        }
    }, [user]);

    const onDeliveryToggle = (deliveryType) => {
        triggerEvent({
            deliveryType,
        });
    };

    const formatLocationText = () => {
        if (isPickup) {
            const { name: store = "" } = area || {};
            return i18next.t("createGroupOrder.pickupTextSub", {
                store: store.replace("Abholung", ""),
            });
        }

        const {
            address: street = "",
            housenumber = "",
            locality = "",
            postcode = "",
        } = address || {};

        return `${street} ${housenumber}, ${postcode} ${locality}`;
    };

    const formatTimeslotObject = (slot) => {
        const slotValid = slot !== null;

        return {
            slot: slotValid
                ? formatTimeslotEntryFromCart(slot.begin, slot.end)
                : "",
            validTill: slotValid ? formatDateToHours(slot.valid_till) : "",
            isToday: slotValid ? isToday(slot.end) : false,
            deliveryDay: slotValid
                ? getLocalisedDayOfWeek(slot.end)
                : undefined,
        };
    };

    const onTimeslotUpdate = ({ slot, earlyOnLocation }) => {
        const { name } = formik.values;
        formik.setValues({
            name,
            earlyOnLocation,
            timeslot: slot,
        });
    };

    const handleCopyToClipboard = async () => {
        const copySuccess = await copy(inviteUrl);
        setCopySuccess((prev) => !prev && copySuccess);
    };

    const onBackClick = () => {
        setStep(CREATE_GROUP_ORDER_STEPS.INFO);
        props.onBackClick();
    };

    const heroImageSrc =
        props.heroImageSrc || getImageAsset("group-order-hero.png");

    const isEarliestTimeslotSelected =
        formik.values.timeslot === null ||
        formik.values.timeslot?.id === earliestTimeslot?.id;

    const layoutProps = {
        ...props,
        isPickup,
        step,
        heroImageSrc,
        location: formatLocationText(),
        onDeliveryToggle,
        earlyOnLocationText: formik.values.earlyOnLocation
            ? i18next.t("timeslotPopup.yesEarly")
            : i18next.t("timeslotPopup.noEarly"),
        earliestPossibleTimeslot: formatTimeslotObject(earliestTimeslot),
        timeslot: formatTimeslotObject(formik.values.timeslot),
        isEarliestTimeslot: isEarliestTimeslotSelected,
        name: formik.values.name,
        setName: (e) => formik.setFieldValue("name", e.target.value),
        openTimeslots: () => setOpenTimeslotPopup(true),
        selectEarliestTimeslot: () => {
            onTimeslotUpdate({
                slot: earliestTimeslot,
                earlyOnLocation: false,
            });
        },
        formErrors: formik.errors,
        submitGroupOrder: formik.submitForm,
        openQrCode: () => setQrPopup(true),
        groupOrderUrl: inviteUrl,
        copyGroupOrderUrl: handleCopyToClipboard,
        onBackClick: onBackClick,
        mobileShouldHide: timeslotPopupOpen || qrOpen,
        isSubmitting: loading,
    };

    return (
        <div id="create-grp-order">
            {isDesktop ? (
                <div className="create-grp-order__overlay--desktop">
                    <CreateGroupOrderWebLayout {...layoutProps} />
                </div>
            ) : (
                <div className="create-grp-order__overlay--mobile">
                    <CreateGroupOrderMobileLayout {...layoutProps} />
                </div>
            )}
            <TimeslotPopup
                popupOpen={timeslotPopupOpen}
                setOpen={setOpenTimeslotPopup}
                handleSubmit={onTimeslotUpdate}
                firstAvailableId={earliestTimeslot?.id}
                buffer={GROUP_ORDER_BUFFER}
            />
            <QrCodePopup
                popupOpen={qrOpen}
                setOpen={setQrPopup}
                qrcode={inviteUrl}
            />
            <NotificationPopup
                open={copySuccessPopup}
                onClose={() => setCopySuccess(false)}>
                <div className="create-grp-order__copy-success">
                    <div className="create-grp-order__copy-icon">
                        <Check />
                    </div>
                    <div className="create-grp-order__copy-info">
                        <span>{props.labels.linkCopied}</span>
                        <p>{props.labels.shareLink}</p>
                    </div>
                </div>
            </NotificationPopup>
        </div>
    );
};

CreateGroupOrder.propTypes = CreateGroupOrderPropTypes;
CreateGroupOrder.defaultProps = {
    labels: CreateGroupOrderLabels,
};
export default CreateGroupOrder;
